// A scan has completed all processing stages and is ready to be used in a model version.
export const SCAN_PROCESSING_SUCCESS = "scan-processing-success";
export const AI_ANALYSIS_IN_PROGRESS = "ai-analysis-in-progress";

// AI inference finished; a Duallo admin must accept (release) or reject the results.
// Customers see this as still-processing — only Duallo admins see the real state.
export const AWAITING_DUALLO_REVIEW = "awaiting-duallo-review";

const statusMap = {
    "scan-record-created": {
        text: "Preparing Upload",
        desc: "The scan record was created and the source video is about to upload.",
        color: "#f57c00",
        processing: true
    },

    "scan-file-uploading": {
        text: "Uploading Source File",
        desc: "The source video is uploading to storage.",
        color: "#f57c00",
        processing: true
    },

    "awaiting-upload-of-source-file": {
        text: "No Source File Uploaded",
        desc: "A source video has not been uploaded yet.",
        color: "#607d8b"
    },

    "source-file-uploaded-failed": {
        text: "Source File Failed to Upload",
        desc: "The source file upload did not complete successfully.",
        color: "#f44336"
    },

    "source-file-uploaded-success": {
        text: "Source File Uploaded",
        desc: "The source file is available and waiting for pipeline processing.",
        color: "#607d8b"
    },

    "processing-scan-start-failed": {
        text: "Scan Processing Failed",
        desc: "The scan did not start successfully.",
        color: "#f44336"
    },

    "processing-scan-start-success": {
        text: "In Queue for Processing",
        desc: "The scan is queued and waiting for a processing server to spool up. The next stage starts automatically once it does.",
        color: "#7e57c2"
    },

    "processing-source-file-copy": {
        text: "Processing Source File",
        desc: "The uploaded source is being copied or converted into a pipeline-ready video.",
        color: "#f57c00",
        processing: true
    },

    "processing-source-file-converting": {
        text: "Processing Source File",
        desc: "The uploaded source is being copied or converted into a pipeline-ready video.",
        color: "#f57c00",
        processing: true
    },

    "pipeline-video-file-ready": {
        text: "Pipeline Video Ready",
        desc: "The pipeline-ready video is available. You can start model generation now.",
        color: "#2196f3"
    },

    "pipeline-video-file-failed": {
        text: "Pipeline Video Failed",
        desc: "The pipeline-ready video could not be created.",
        color: "#f44336"
    },

    "model-generation-in-progress": {
        text: "Model Generation In Progress",
        desc: "The system is currently generating the model from the processed scan assets.",
        color: "#f57c00",
        processing: true
    },

    "model-generation-success": {
        text: "Model Generation Complete",
        desc: "The model generation stage completed successfully.",
        color: "#2196f3"
    },

    "model-generation-failed": {
        text: "Model Generation Failed",
        desc: "The model generation stage failed and may need to be retried.",
        color: "#f44336"
    },

    "ai-analysis-in-progress": {
        text: "AI Analysis In Progress",
        desc: "AI analysis is currently running on the scan outputs.",
        color: "#f57c00",
        processing: true
    },

    "ai-analysis-success": {
        text: "AI Analysis Complete",
        desc: "AI analysis completed successfully.",
        color: "#2196f3"
    },

    "ai-analysis-failed": {
        text: "AI Analysis Failed",
        desc: "AI analysis failed before completion.",
        color: "#f44336"
    },

    // Duallo-admin-facing entry; getScanStatusConfig remaps it to the
    // ai-analysis-in-progress presentation for everyone else.
    "awaiting-duallo-review": {
        text: "Waiting for Duallo Review",
        desc: "AI analysis is complete. The results are being reviewed by Duallo before the scan is released.",
        color: "#ab47bc"
    },

    "scan-processing-success": {
        text: "Scan Processing Complete",
        desc: "All major scan processing stages completed successfully.",
        color: "#4caf50"
    },

    "scan-processing-failed": {
        text: "Scan Processing Failed",
        desc: "The scan failed during one of the processing stages.",
        color: "#f44336"
    }
};

export function sourceUploadStage(status) {
    if (status === "awaiting-upload-of-source-file") return "Waiting";
    if (status === "scan-record-created" || status === "scan-file-uploading") return "In Progress";
    if (status === "source-file-uploaded-failed") return "Failed";
    if (
        status === "source-file-uploaded-success" ||
        status === "processing-scan-start-success" ||
        status === "processing-scan-start-failed" ||
        status === "processing-source-file-copy" ||
        status === "processing-source-file-converting" ||
        status === "pipeline-video-file-ready" ||
        status === "model-generation-in-progress" ||
        status === "model-generation-success" ||
        status === "model-generation-failed" ||
        status === "ai-analysis-in-progress" ||
        status === "awaiting-duallo-review" ||
        status === "ai-analysis-success" ||
        status === "ai-analysis-failed" ||
        status === "scan-processing-success" ||
        status === "scan-processing-failed"
    ) return "Complete";

    return "Unknown";
}

export function sourceProcessingStage(status) {
    if (status === "processing-scan-start-success") return "Queued";

    if (
        status === "processing-source-file-copy" ||
        status === "processing-source-file-converting"
    ) return "In Progress";

    if (
        status === "pipeline-video-file-ready" ||
        status === "model-generation-in-progress" ||
        status === "model-generation-success" ||
        status === "model-generation-failed" ||
        status === "ai-analysis-in-progress" ||
        status === "awaiting-duallo-review" ||
        status === "ai-analysis-success" ||
        status === "ai-analysis-failed" ||
        status === "scan-processing-success" ||
        status === "scan-processing-failed"
    ) return "Complete";

    if (status === "pipeline-video-file-failed" ||
        status === "processing-scan-start-failed"
    ) return "Failed";

    return "Waiting";
}

export function pipelineVideoStage(status) {
    if (status === "pipeline-video-file-ready") return "Ready";
    if (status === "pipeline-video-file-failed") return "Failed";

    if (
        status === "model-generation-in-progress" ||
        status === "model-generation-success" ||
        status === "model-generation-failed" ||
        status === "ai-analysis-in-progress" ||
        status === "awaiting-duallo-review" ||
        status === "ai-analysis-success" ||
        status === "ai-analysis-failed" ||
        status === "scan-processing-success" ||
        status === "scan-processing-failed"
    ) return "Complete";

    return "Waiting";
}

export function modelGenerationStage(status) {
    if (status === "model-generation-in-progress") return "In Progress";
    if (status === "model-generation-success") return "Complete";
    if (status === "model-generation-failed") return "Failed";

    if (
        status === "ai-analysis-in-progress" ||
        status === "awaiting-duallo-review" ||
        status === "ai-analysis-success" ||
        status === "ai-analysis-failed" ||
        status === "scan-processing-success" ||
        status === "scan-processing-failed"
    ) return "Complete";

    return "Waiting";
}

export function aiAnalysisStage(status, isDualloAdmin = false) {
    if (status === "awaiting-duallo-review") return isDualloAdmin ? "Complete" : "In Progress";
    if (status === "ai-analysis-in-progress") return "In Progress";
    if (status === "ai-analysis-success") return "Complete";
    if (status === "ai-analysis-failed") return "Failed";
    if (
        status === "scan-processing-success" ||
        status === "scan-processing-failed"
    ) return "Complete";

    return "Waiting";
}

export function overallStage(status, isDualloAdmin = false) {
    if (status === "awaiting-duallo-review") return isDualloAdmin ? "In Review" : "In Progress";
    if (status === "scan-processing-success") return "Complete";
    if (status === "scan-processing-failed") return "Failed";
    if (
        status.includes("in-progress") ||
        status.startsWith("processing-") ||
        status === "scan-record-created" ||
        status === "scan-file-uploading" ||
        status === "source-file-uploaded-success"
    ) return "In Progress";

    return "Pending";
}

export function stageClass(stage) {
    const normalized = (stage || "").toLowerCase();

    if (normalized === "complete" || normalized === "ready") return "stage-success";
    if (normalized === "failed") return "stage-failed";
    if (normalized === "in progress") return "stage-active";
    if (normalized === "in review") return "stage-queued";
    if (normalized === "queued") return "stage-queued";
    if (normalized === "pending" || normalized === "waiting") return "stage-pending";

    return "stage-default";
}

export function canGenerate(status) {
    return status === "pipeline-video-file-ready";
}

export function normalizeScanStatus(status) {
    return (status || "").toLowerCase().trim();
}

export function getScanStatusConfig(status, isDualloAdmin = false) {
    let normalized = normalizeScanStatus(status);

    // Scans in Duallo review must look like they are still processing to
    // anyone who isn't Duallo staff.
    if (normalized === AWAITING_DUALLO_REVIEW && !isDualloAdmin)
        normalized = AI_ANALYSIS_IN_PROGRESS;

    return statusMap[normalized] || {
        text: status || "Unknown",
        color: "#37474f",
        processing: false
    };
}

export function getScanStatusText(status, isDualloAdmin = false) {
    return getScanStatusConfig(status, isDualloAdmin).text;
}

export function getScanStatusColor(status, isDualloAdmin = false) {
    return getScanStatusConfig(status, isDualloAdmin).color;
}

export function getScanStatusDescription(status, isDualloAdmin = false) {
    return getScanStatusConfig(status, isDualloAdmin).desc;
}

export function isScanStatusProcessing(status, isDualloAdmin = false) {
    return getScanStatusConfig(status, isDualloAdmin).processing === true;
}

export default statusMap;